import axios from 'axios';

// Detectar ambiente automaticamente
const isProduction = window.location.hostname !== 'localhost';

// Configurar a URL base da API de acordo com o ambiente
const getApiBaseUrl = () => {
    if (process.env.NODE_ENV === 'production') {
        // Em produção, a API está na mesma origem, no diretório /api
        return '/api';
    }
    // Em desenvolvimento, o proxy redireciona para o servidor PHP (ex: http://localhost:8080)
    // e precisamos especificar a pasta do projeto.
    return '/catalogo/api'; 
};

// Criar instância do axios com configurações base
const api = axios.create({
  baseURL: getApiBaseUrl(),
  timeout: 30000, // 30 segundos
  headers: {
    'Content-Type': 'application/json'
  },
});

// Interceptor de resposta para tratamento de erros e retry para porta alternativa em dev
api.interceptors.response.use(
  response => response,
  async error => {
    const originalRequest = error.config;
    // Tentar porta alternativa (3002) para localhost:3001 em caso de erro de rede, e se não for um retry
    if (error.message && error.message.includes('Network Error') &&
        originalRequest && originalRequest.url && 
        originalRequest.baseURL && originalRequest.baseURL.includes('localhost:3001') && // Checar baseURL também
        !originalRequest._retryAttempted) { // Adiciona flag para evitar loop
      
      console.warn('Erro de conexão na porta 3001, tentando porta alternativa 3002...');
      originalRequest._retryAttempted = true; // Marcar que a tentativa foi feita
      
      const alternativeBaseURL = originalRequest.baseURL.replace('3001', '3002');
      // Manter a URL original completa se ela for absoluta, ou recalcular se for relativa
      const newUrl = originalRequest.url.startsWith('http') 
        ? originalRequest.url.replace(originalRequest.baseURL, alternativeBaseURL)
        : originalRequest.url;

      const newConfig = {
        ...originalRequest,
        baseURL: alternativeBaseURL,
        url: newUrl 
      };

      try {
        return await axios(newConfig); 
      } catch (retryError) {
        console.error('Erro ao tentar porta alternativa 3002:', retryError.response?.data || retryError.message);
        return Promise.reject(retryError); 
      }
    }
    
    console.error('Erro na API:', error.response?.data || error.message || error);
    return Promise.reject(error);
  }
);

// Interceptor para requisições (logs e transformações de dados)
api.interceptors.request.use(
  config => {
    // Construir a URL completa para o log
    let fullUrl = config.url;
    if (config.baseURL && !config.url.startsWith('http')) {
      fullUrl = config.baseURL + (config.url.startsWith('/') ? '' : '/') + config.url;
    }


    // Tratamento para atualização de produto
    if (config.url && config.url.includes('/products-standalone/update') && config.method === 'post') {
      if (config.data) {
        const data = config.data;
        data.price = parseFloat(data.price) || 0;
        data.salePrice = parseFloat(data.salePrice) || 0;
        data.stock = parseInt(data.stock) || 0;
        data.active = data.active === true;
        data.description = String(data.description || '');
        data.name = String(data.name || '');
        data.category = String(data.category || '');
        data.tag = String(data.tag || '');
        data.code = String(data.code || '');
      }
    }
    return config;
  },
  error => {
    console.error('Erro na requisição:', error);
    return Promise.reject(error);
  }
);

// A lógica de cache de produtos baseada em JSON foi removida.
// O sistema agora depende exclusivamente da API do backend para buscar produtos.

// Função para buscar produtos do backend (nova lógica)
export const getProducts = async (page = 1, limit = 20, category = null, search = null, sortBy = 'name', sortOrder = 'ASC') => {
  console.log('Chamando getProducts (frontend) com:', { page, limit, category, search, sortBy, sortOrder });
  try {
    let endpoint = search ? `/search.php` : `/all.php`;
    const params = {
      page,
      limit,
      sortBy,
      sortOrder
    };

    if (search && search.trim() !== '') {
      // Se houver um termo de busca, usa a rota de pesquisa e o parâmetro 'q'
      params.q = search.trim();
      console.log(`Buscando no backend com termo: ${search}`);
      // Permite filtrar a busca por categoria, se fornecido
      if (category) {
        params.category = category;
      }
    } else if (category) {
      // Se não houver busca, mas houver categoria, usa a rota 'all' com filtro de categoria
      params.category = category;
      console.log(`Buscando no backend pela categoria: ${category}`);
    }

    const response = await api.get(endpoint, { params });

    // A resposta do backend já deve vir com o caminho completo da imagem
    // e os dados no formato correto.
    if (response.data && response.data.products) {
      console.log(`Recebidos ${response.data.products.length} produtos do backend.`);
      return response.data; // Retorna { products: [...], pagination: {...} }
    } else {
      // Se a resposta não tiver o formato esperado, retorna um objeto vazio
      console.warn('Resposta da API de produtos não contém a estrutura esperada:', response.data);
      return { products: [], pagination: { total: 0, totalPages: 1, page: 1, limit: limit } };
    }

  } catch (apiError) {
    console.error(`Erro ao buscar produtos da API do backend (getProducts):`, apiError.response?.data || apiError.message);
    // Em caso de erro, retorna um objeto vazio para não quebrar o frontend
    return { products: [], pagination: { total: 0, totalPages: 1, page: 1, limit: limit } };
  }
};

// Função para buscar um produto específico
// Função para buscar um produto específico diretamente da API backend
export const getProductByCode = async (code) => {
  console.log('Buscando produto com código (API):', code);
  if (!code) {
    console.error('Código de produto inválido fornecido para getProductByCode:', code);
    return null;
  }
  // Normaliza o código para string, remove espaços extras e converte para minúsculas para consistência na busca
  const codeStr = String(code).trim().toLowerCase();
  console.log('Código normalizado para busca (API):', codeStr);

  try {
    // Chama diretamente a API do backend para buscar o produto pelo código
    // A API PHP espera o código como um parâmetro de query, não na URL
    const response = await api.get(`/product.php`, { params: { code } });
    
    // Verifica se a resposta contém dados e se o objeto de dados não está vazio
    if (response.data && Object.keys(response.data).length > 0) {
      console.log('Produto encontrado na API do backend:', response.data.name);
      return response.data; // Retorna os dados do produto
    }
    
    // Se não encontrou ou a resposta foi vazia (mas sem erro HTTP)
    console.log(`Produto com código "${codeStr}" não encontrado na API do backend (getProductByCode).`);
    return null;
  } catch (apiError) {
    // Trata erros da chamada à API (ex: 404, 500 do backend)
    console.error(`Erro ao buscar produto "${codeStr}" da API do backend (getProductByCode):`, apiError.response?.data || apiError.message);
    return null;
  }
};

// Função para buscar categorias do backend
export const getCategories = async () => {
  try {
    const response = await api.get('categories.php'); 
    return response.data || [];
  } catch (error) {
    console.error('Erro ao buscar categorias:', error.response?.data || error.message);
    return []; 
  }
};

// Função específica para atualizar produtos no backend
export const updateProduct = async (productData) => {
  console.log('Iniciando atualização do produto:', productData);
  try {
    const preparedProduct = {
      id: productData.id, 
      code: String(productData.code || '').trim(),
      name: String(productData.name || '').trim(),
      price: parseFloat(productData.price) || 0,
      salePrice: parseFloat(productData.salePrice || 0) || 0, 
      stock: parseInt(productData.stock) || 0,
      active: productData.active === true, 
      description: String(productData.description || '').trim(),
      tag: String(productData.tag || '').trim(),
      category: String(productData.category || '').trim(),
      image_url: productData.image_url || null, 
    };

    Object.keys(preparedProduct).forEach(key => {
      if (preparedProduct[key] === undefined) {
        delete preparedProduct[key];
      }
    });
    
    console.log('Produto preparado para atualização:', preparedProduct);
    const response = await api.put('all.php', preparedProduct);
    console.log('Resposta da atualização:', response.data);
    return response.data;
  } catch (error) {
    console.error('Erro ao atualizar produto:', error.response?.data || error.message || error);
    throw new Error(error.response?.data?.message || 'Falha ao atualizar o produto. Verifique os dados e tente novamente.');
  }
};

// Função para solicitar a sincronização de produtos com o SQL Server
export const syncProductsWithSqlServer = async () => {
  console.log('Solicitando sincronização de produtos com o SQL Server via API...');
  try {
    const response = await api.post('/products-standalone/sync-from-sql');
    console.log('Resposta da sincronização:', response.data);
    return response.data; // Espera-se { success: true, message: '...' } ou { success: false, message: '...' }
  } catch (error) {
    console.error('Erro ao solicitar sincronização com SQL Server:', error.response?.data || error.message || error);
    throw new Error(error.response?.data?.message || 'Falha ao solicitar sincronização com o SQL Server.');
  }
};


// Função para gerar descrição de produto com IA
export const generateProductDescriptionAI = async (productData) => {
  console.log('Solicitando descrição de produto com IA via API...', productData);
  try {
    const response = await api.post('/ai/generate-description', productData);
    console.log('Resposta da geração de descrição AI:', response.data);
    return response.data; // Espera-se { description: '...' }
  } catch (error) {
    console.error('Erro ao solicitar descrição de produto com IA:', error.response?.data || error.message || error);
    throw new Error(error.response?.data?.message || 'Falha ao gerar descrição com IA.');
  }
};

// Função para atualizar descrições de múltiplos produtos em lote
export const bulkUpdateProductDescriptions = async (productsData) => {
  try {
    console.log('API: Chamando bulkUpdateProductDescriptions com:', productsData);
    const response = await api.put('/products-standalone/bulk-update-descriptions', productsData);
    console.log('API: Resposta de bulkUpdateProductDescriptions:', response.data);
    return response.data;
  } catch (error) {
    console.error('API Error: Falha ao atualizar descrições em lote', error.response ? error.response.data : error.message);
    throw error.response ? error.response.data : new Error('Falha ao atualizar descrições em lote');
  }
};

// Função para gerar descrições em massa com IA
export const bulkGenerateAIDescriptions = async (productsData) => {
  try {
    console.log('API: Chamando bulkGenerateAIDescriptions com:', productsData);
    // Usar configuração personalizada com timeout maior para operações de IA
    const response = await api.post('/ai/bulk-generate-descriptions', { products: productsData }, {
      timeout: 120000 // 2 minutos para operações de IA em massa
    });
    console.log('API: Resposta de bulkGenerateAIDescriptions:', response.data);
    return response.data;
  } catch (error) {
    console.error('API Error: Falha ao gerar descrições em massa com IA', error.response ? error.response.data : error.message);
    throw error.response ? error.response.data : new Error('Falha ao gerar descrições em massa com IA');
  }
};

export default api;
